import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import useDialog from 'hooks/useDialog';
import { RejectedStatus } from '../ApplicationStatusControl';
import { DateTimeInput, SimpleForm, Toolbar, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { ProfessionalDisplayHelper } from 'utils/displayHelpers/professional';
import { IProfessional } from '@medely/types';
import ScreenerAccountInput from 'components/Filter/ScreenerAccountInput';

type RejectFormValues = {
  screener_account_id: number | null;
  scheduled_time: string | null;
};

type ProfessionalScreening = {
  id: number;
  screening_type: 'phone' | 'written';
  canceled_at: string | null;
  reason: 'application' | 're_screening';
  no_show: boolean;
  scheduled_time: string | null;
  screener_account_id: number | null;
};

type RejectPayload = {
  application_status: typeof RejectedStatus;
  professional_id?: number;
  screener_account_id?: number;
  scheduled_time?: string;
  reason?: string;
  screening_type?: 'phone' | 'written';
};

interface RejectItemProps {
  updateApplicationStatus: (val: RejectPayload) => void;
}

const PROFESSIONAL_TYPE = {
  AGENCY: 'AGENCY',
  IRP: 'IRP',
  MARKETPLACE: 'MARKETPLACE',
} as const;

const reasonChoices = [
  'Conflict of Interest',
  'Deactivated Account',
  'Duplicate Account',
  'Falsifying Document',
  'No relevant experience',
  'Not a good fit - Bad Attitude',
  'Test Account',
];

const RejectItem = ({ updateApplicationStatus }: RejectItemProps) => {
  const { open, closeDialog, openDialog } = useDialog();

  return (
    <>
      <MenuItem onClick={openDialog}>Reject</MenuItem>
      <RejectDialog
        open={open}
        closeDialog={closeDialog}
        updateApplicationStatus={updateApplicationStatus}
      />
    </>
  );
};

const RejectToolBar = ({ closeDialog, rejectReason }) => {
  const { getValues } = useFormContext();
  const { screener_account_id, scheduled_time, screening_type } = getValues();
  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button onClick={closeDialog}>Cancel</Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={
          !rejectReason || !screener_account_id || (!scheduled_time && screening_type === 'phone')
        }
      >
        Submit
      </Button>
    </Toolbar>
  );
};

const RejectDialog = ({ open, closeDialog, updateApplicationStatus }) => {
  const [rejectReason, setRejectReason] = useState('');
  const record: IProfessional = useRecordContext();

  const latestScreening = (record as any)?.professional_screenings?.find(
    (screening: ProfessionalScreening) =>
      screening?.screening_type === 'phone' &&
      !screening?.canceled_at &&
      !screening?.no_show &&
      screening?.scheduled_time,
  );

  const defaultValues = {
    screener_account_id: latestScreening?.screener_account_id || null,
    scheduled_time: latestScreening?.scheduled_time || null,
  };

  const submitReject = ({ screener_account_id, scheduled_time }: RejectFormValues) => {
    const hasScreeningChanges =
      screener_account_id !== defaultValues.screener_account_id ||
      scheduled_time !== defaultValues.scheduled_time;

    const payload: RejectPayload = {
      application_status: RejectedStatus,
      reject_reason: rejectReason,
    };

    if (hasScreeningChanges) {
      payload.professional_id = Number(record?.id);
      payload.screener_account_id = screener_account_id ?? undefined;
      payload.scheduled_time = scheduled_time ?? undefined;
      payload.reason = 'application';
      payload.screening_type = latestScreening?.screening_type || 'written';
    }

    updateApplicationStatus(payload);
  };

  const getProfessionalTypeString = (proType: string) => {
    const type = proType?.toUpperCase();
    if (type === PROFESSIONAL_TYPE.AGENCY) {
      const agencyName = record?.account.agency?.name;
      return `Agency${agencyName ? ` (${agencyName})` : ''}`;
    }
    if (type === PROFESSIONAL_TYPE.IRP) {
      const companyName = record?.account.company?.name;
      return `IRM${companyName ? ` (${companyName})` : ''}`;
    }
    if (type === PROFESSIONAL_TYPE.MARKETPLACE) {
      return 'Marketplace';
    } else {
      return 'professional';
    }
  };

  const fullName = (professional: IProfessional) => {
    return (
      professional?.account &&
      `${professional.account.first_name} ${professional.account.last_name}`
    );
  };

  const professionalType = record ? new ProfessionalDisplayHelper(record).type : '';
  const professionalName = record ? fullName(record) : '';

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>Reject Reason</DialogTitle>
      <DialogContent>
        <Typography>
          Professional Name:{' '}
          <Typography component="span" color="error" fontWeight="bold">
            {professionalName}
          </Typography>
          <br />
          Professional Type:{' '}
          <Typography component="span" color="error" fontWeight="bold">
            {getProfessionalTypeString(professionalType)}
          </Typography>
        </Typography>
      </DialogContent>
      <SimpleForm
        key={open ? 'open' : 'closed'}
        onSubmit={submitReject}
        toolbar={<RejectToolBar closeDialog={closeDialog} rejectReason={rejectReason} />}
        defaultValues={defaultValues}
      >
        <Select
          fullWidth
          value={rejectReason}
          label="Select Reject Reason"
          onChange={({ target }) => setRejectReason(target.value as string)}
          required
          style={{ width: '550px', marginBottom: '20px' }}
        >
          {reasonChoices.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {reason}
            </MenuItem>
          ))}
        </Select>
        <ScreenerAccountInput
          source="screener_account_id"
          label="Screening Recruiter"
          alwaysOn
          fullWidth
        />
        <DateTimeInput label="Screening Date And Time" source="scheduled_time" fullWidth />
      </SimpleForm>
    </Dialog>
  );
};

export default RejectItem;
